'use client'

import { useEffect, useState, type RefObject } from 'react'

export type StickyPhase = 'hidden' | 'ghost' | 'stuck'

export interface UseStickyPhaseOptions {
  /** Pixel offset from viewport top at which the element is considered "stuck". */
  stickyTopPx?: number
}

/**
 * Tracks an element's scroll-position phase via `getBoundingClientRect`:
 *   `hidden` — not in viewport
 *   `ghost`  — in viewport but not yet pinned
 *   `stuck`  — pinned at (or above) the sticky-top offset
 *
 * Used by `<InPageNav>` to drive its three-stage opacity reveal. Exposed
 * separately because the same phase machine is useful for any sticky chrome
 * (sub-headers, floating toolbars, table-of-contents pills, …).
 */
export function useStickyPhase<T extends HTMLElement>(
  ref: RefObject<T | null>,
  { stickyTopPx = 80 }: UseStickyPhaseOptions = {},
): StickyPhase {
  const [phase, setPhase] = useState<StickyPhase>('hidden')

  useEffect(() => {
    const check = () => {
      const el = ref.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const inViewport = rect.top < window.innerHeight && rect.bottom > 0
      const isStuck = inViewport && rect.top <= stickyTopPx + 1
      setPhase(!inViewport ? 'hidden' : isStuck ? 'stuck' : 'ghost')
    }
    check()
    window.addEventListener('scroll', check, { passive: true })
    window.addEventListener('resize', check)
    return () => {
      window.removeEventListener('scroll', check)
      window.removeEventListener('resize', check)
    }
  }, [ref, stickyTopPx])

  return phase
}
